"use client";

import { Link } from "@tanstack/react-router";
import { Cookie, X } from "lucide-react";
import { useEffect, useState } from "react";

import { Button } from "@super-resume-new/ui/components/button";

const STORAGE_KEY = "jobcues_cookie_consent";
export const OPEN_COOKIE_CONSENT_EVENT = "jobcues:open-cookie-consent";

export function openCookieConsent() {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent(OPEN_COOKIE_CONSENT_EVENT));
  }
}

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
  }
}

export function CookieConsent() {
  const [isOpen, setIsOpen] = useState(false);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    const stored = localStorage.getItem(STORAGE_KEY);

    if (stored === "accepted") {
      window.gtag?.("consent", "update", { analytics_storage: "granted" });
    } else if (stored === "declined") {
      window.gtag?.("consent", "update", { analytics_storage: "denied" });
    } else {
      setIsOpen(true);
    }

    const handleOpen = () => setIsOpen(true);
    window.addEventListener(OPEN_COOKIE_CONSENT_EVENT, handleOpen);
    return () => window.removeEventListener(OPEN_COOKIE_CONSENT_EVENT, handleOpen);
  }, []);

  const handleAccept = () => {
    localStorage.setItem(STORAGE_KEY, "accepted");
    window.gtag?.("consent", "update", { analytics_storage: "granted" });
    setIsOpen(false);
  };

  const handleDecline = () => {
    localStorage.setItem(STORAGE_KEY, "declined");
    window.gtag?.("consent", "update", { analytics_storage: "denied" });
    setIsOpen(false);
  };

  if (!mounted || !isOpen) {
    return null;
  }

  return (
    <aside
      aria-label="Cookie consent banner"
      role="region"
      className="fixed bottom-4 right-4 sm:bottom-6 sm:right-6 z-50 max-w-md w-[calc(100vw-2rem)] rounded-xl border border-border bg-card p-5 shadow-sm text-card-foreground transition-all duration-200"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2">
          <Cookie className="size-4 text-violet-500" aria-hidden="true" />
          <h2 className="text-sm font-semibold text-foreground">Cookie preferences</h2>
        </div>
        <button
          type="button"
          onClick={handleDecline}
          aria-label="Close cookie consent banner"
          className="rounded-md p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground cursor-pointer"
        >
          <X className="size-4" aria-hidden="true" />
        </button>
      </div>

      <p className="mt-2.5 text-[13px] leading-relaxed text-muted-foreground">
        We use essential cookies to make this site work. With your consent, we also use analytics
        cookies to understand how the site is used. You can change your choice at any time. Learn
        more in our{" "}
        <Link
          to="/privacy"
          hash="section-6"
          className="font-medium text-foreground underline underline-offset-4 transition-colors hover:text-violet-500"
        >
          Privacy policy
        </Link>
        .
      </p>

      <div className="mt-4 flex items-center justify-end gap-2.5">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={handleDecline}
          className="text-xs"
        >
          Essential only
        </Button>
        <Button
          type="button"
          size="sm"
          onClick={handleAccept}
          className="text-xs"
        >
          Accept all
        </Button>
      </div>
    </aside>
  );
}
